// Vue
import { ref } from "vue";

// Dependencies
import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";

// Types
import { BuildingTypesEntry, BuildingTypesType } from "../types/BuildingTypesType";

// GraphQL
import { GetBuildingTypesQuery } from "@/graphql";

interface BuildingTypesProvider {
  // Getters
  getBuildingTypes: () => BuildingTypesEntry[];

  // Methods
  mapData: (data: GetBuildingTypesQuery) => void;

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useBuildingTypes = defineStore("BuildingTypesProvider", (): BuildingTypesProvider => {
  // ///////////////////
  // Refs
  // ///////////////////

  const defaultModels = ref<BuildingTypesEntry[]>([]);

  // ///////////////////
  // Getters
  // ///////////////////

  function getBuildingTypes() {
    return defaultModels.value;
  }

  // ///////////////////
  // Setters
  // ///////////////////

  function setBuildingTypes(newModels: ReturnType<typeof getBuildingTypes>) {
    defaultModels.value = newModels;
  }

  // ///////////////////
  // Methods
  // ///////////////////

  function mapData(data: GetBuildingTypesQuery) {
    try {
      const { buildingType } = data;
      if (!buildingType) {
        return;
      }

      // group parents with their children so they can be displayed in order
      const parentsAndChildren = new Map<string, [BuildingTypesType | null, BuildingTypesType[]]>();
      buildingType.forEach((element) => {
        if (!element.parent) {
          const id = element.id.toString(10);
          const siblings = parentsAndChildren.get(id) || [element, []];
          if (siblings[0] === null) {
            siblings[0] = element;
          }
          parentsAndChildren.set(id, siblings);
        } else {
          const parentId = element.parent.id.toString(10);
          const siblings = parentsAndChildren.get(parentId) || [null, []];
          siblings[1].push(element);
          parentsAndChildren.set(parentId, siblings);
        }
      });

      const sortedBuildingTypes: ReturnType<typeof getBuildingTypes> = [];
      Array.from(parentsAndChildren.entries()).forEach(([_key, [parent, children]]) => {
        const getLabel = (x: any) => (x.code ? `(${x.code}) ${x.label}` : `${x.label}`);

        // this should never happen but typescript doesn't know
        if (!parent) {
          return;
        }

        sortedBuildingTypes.push({
          id: parent.id.toString(10),
          label: getLabel(parent),
          disabled: children.length > 0,
        });
        children
          .sort((x: any, y: any) => x.label.localeCompare(y.label))
          .forEach((child: BuildingTypesType) => {
            const entry = {
              id: child.id.toString(10),
              label: "\u00A0\u00A0\u00A0\u00A0" + getLabel(child),
              disabled: false,
            };
            sortedBuildingTypes.push(entry);
          });
      });

      setBuildingTypes(sortedBuildingTypes);
      // Mapped data means the load is over, wherever the data came from — the fetcher component in
      // the app, or a direct seed in stories. The start dialog disables its selects while a
      // country's types load, so the flip must accompany the data.
      setLoading(false);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const loading = ref<boolean>(false);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // ///////////////////
  // Returns
  // ///////////////////

  return {
    // Getters
    getBuildingTypes,

    // Methods
    mapData,

    // Utility
    getLoading,
    setLoading,
  };
});

export default useBuildingTypes;
